import type { ChatMessage, ChatToolCall, ChatTranscriptItem, ToolCallOutcome } from "~/ai-chat/model/chat-message";
import { formatToolCallDuration, toolCallSource } from "~/ai-chat/model/tool-activity";

const ROLE_LABEL: Record<ChatMessage["role"], string> = {
  user: "You",
  assistant: "Istari",
  system: "System",
};

/**
 * Render a chat transcript as Markdown for "save chat as a resource". Only
 * message turns are included — transient error bubbles are dropped. A turn's
 * tool calls follow it as fenced code blocks.
 */
export function chatTranscriptToMarkdown(title: string, items: ChatTranscriptItem[]): string {
  const heading = `# ${title.trim() || "Istari chat"}`;
  const turns = items
    .filter((item): item is ChatMessage => item.kind === "message")
    // trimEnd (not trim): preserve leading whitespace/newlines so indented code blocks and other
    // structure a turn opens with survive the round-trip to Markdown.
    .map((message) =>
      [
        `**${ROLE_LABEL[message.role]}:** ${message.content.trimEnd()}`,
        ...(message.toolCalls ?? []).map(toolCallMarkdown),
      ].join("\n\n"),
    );
  return `${[heading, ...turns].join("\n\n")}\n`;
}

/** Safe `.md` filename derived from the chat title. */
export function chatResourceFilename(title: string): string {
  const slug = title
    .trim()
    .replace(/[^\w.-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
  return `${slug || "istari-chat"}.md`;
}

function toolCallMarkdown(call: ChatToolCall): string {
  const source = toolCallSource(call);
  const code =
    source === null
      ? `_Tool call: ${call.tool} (arguments too large to show)_`
      : `_Tool call: ${call.tool}_\n\n${fenced(source.text, source.language)}`;
  return call.outcome ? `${code}\n\n${outcomeMarkdown(call.outcome)}` : code;
}

function outcomeMarkdown(outcome: ToolCallOutcome): string {
  const took = outcome.durationMs === null ? "" : ` in ${formatToolCallDuration(outcome.durationMs)}`;
  if (outcome.ok) return `_Returned${took}:_\n\n${fenced(outcome.summary || "(no output)", "text")}`;
  return `_Failed${took}:_\n\n${fenced(outcome.summary, "text")}`;
}

// A fence longer than any backtick run inside the text, so text containing ``` can't close it early.
function fenced(text: string, language: string): string {
  const fence = "`".repeat(Math.max(3, ...Array.from(text.matchAll(/`+/g), (m) => m[0].length + 1)));
  return `${fence}${language}\n${text}\n${fence}`;
}
